import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Card,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import Iconify from '@/components/Iconify';
import Label from '@/components/Label';
import useAuth from '@/hooks/useAuth';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { fDateTime } from '@/utils/formatTime';
import {
  getDashboardUserDuoStatus,
  sendDashboardUserDuoInvite,
} from '@/features/users/shared/api';
import {
  DashboardUserDuoFields,
  DashboardUserDuoStatus,
  User,
} from '@/features/users/shared/types';
import { LabelColors } from '@/types';

type DashboardUserDuoSecurityProps = {
  user?: User | null;
};

type DuoBadge = {
  color: LabelColors;
  label: string;
};

const normalizeStatusValue = (value?: string | null) =>
  value?.trim().toLowerCase().replace(/[\s-]+/g, '_') ?? '';

const hasAnyStatus = (value: string, statuses: string[]) =>
  statuses.includes(normalizeStatusValue(value));

export const getDashboardUserDuoBadge = (
  duoStatus?: DashboardUserDuoFields | null
): DuoBadge => {
  if (duoStatus?.duo_2fa_enabled === true) {
    return { color: 'success', label: '2FA Enabled' };
  }

  const enrollmentStatus = normalizeStatusValue(
    duoStatus?.duo_enrollment_status
  );
  const duoExternalStatus = normalizeStatusValue(duoStatus?.duo?.status);
  const statusValues = [enrollmentStatus, duoExternalStatus];

  if (statusValues.some((status) => hasAnyStatus(status, ['locked_out']))) {
    return { color: 'error', label: 'Locked out' };
  }

  if (
    statusValues.some((status) =>
      hasAnyStatus(status, ['disabled', 'inactive'])
    )
  ) {
    return { color: 'default', label: 'Disabled' };
  }

  if (
    duoStatus?.duo?.is_enrolled === true ||
    enrollmentStatus === 'enrolled' ||
    !!duoStatus?.duo_enrolled_at
  ) {
    return { color: 'info', label: 'Enrolled' };
  }

  if (
    !!duoStatus?.duo_enrollment_sent_at ||
    ['pending', 'invited', 'sent', 'invite_sent', 'pushed'].includes(
      enrollmentStatus
    )
  ) {
    return { color: 'warning', label: 'Pending' };
  }

  return { color: 'default', label: 'Not invited' };
};

const isActiveDashboardUser = (user: User) => {
  const statusValues = [
    user.user_statuses?.value,
    user.user_statuses?.display,
  ].map((status) => normalizeStatusValue(status));

  return Number(user.status) === 1 || statusValues.includes('active');
};

const formatDateValue = (value?: string | null) => {
  if (!value) return 'N/A';

  try {
    return fDateTime(value);
  } catch {
    return value;
  }
};

const formatBooleanValue = (value?: boolean | null) => {
  if (typeof value !== 'boolean') return 'N/A';

  return value ? 'Yes' : 'No';
};

const formatTextValue = (value?: string | null) => value || 'N/A';

function DuoDetailItem({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

export default function DashboardUserDuoSecurity({
  user,
}: DashboardUserDuoSecurityProps) {
  const { isSuperAdmin } = useAuth();
  const { errorMsg, successMsg } = useSnackbarMsg();
  const queryClient = useQueryClient();
  const userId = user?.id;
  const visible = isSuperAdmin && Number(user?.user_type) === 3 && !!userId;

  const { data: fetchedStatus, isLoading } = useQuery({
    queryKey: ['dashboardUserDuoStatus', userId],
    queryFn: () => getDashboardUserDuoStatus(Number(userId)),
    enabled: visible,
    onError: (error) =>
      errorMsg(error, 'There was an error while fetching Duo status'),
    refetchOnWindowFocus: false,
  });

  const duoStatus = useMemo<DashboardUserDuoStatus>(
    () => ({
      duo_2fa_enabled:
        fetchedStatus?.duo_2fa_enabled ?? user?.duo_2fa_enabled,
      duo_enrollment_status:
        fetchedStatus?.duo_enrollment_status ?? user?.duo_enrollment_status,
      duo_enrollment_sent_at:
        fetchedStatus?.duo_enrollment_sent_at ??
        user?.duo_enrollment_sent_at,
      duo_enrolled_at:
        fetchedStatus?.duo_enrolled_at ?? user?.duo_enrolled_at,
      duo_last_status_synced_at:
        fetchedStatus?.duo_last_status_synced_at ??
        user?.duo_last_status_synced_at,
      duo: fetchedStatus?.duo ?? user?.duo,
    }),
    [fetchedStatus, user]
  );

  const updateDuoStatusCache = (data: DashboardUserDuoStatus) => {
    queryClient.setQueryData(['dashboardUserDuoStatus', userId], data);
    queryClient.invalidateQueries(['dashboardUsers']);
    queryClient.invalidateQueries(['dashboardUsers', String(userId)]);
    queryClient.invalidateQueries(['dashboardUsers', userId]);
  };

  const inviteMutation = useMutation({
    mutationFn: () => sendDashboardUserDuoInvite(Number(userId)),
    onSuccess: (data) => {
      updateDuoStatusCache(data);
      successMsg('Duo invite sent');
    },
    onError: (error) =>
      errorMsg(error, 'There was an error while sending Duo invite'),
  });

  const refreshMutation = useMutation({
    mutationFn: () => getDashboardUserDuoStatus(Number(userId)),
    onSuccess: (data) => {
      updateDuoStatusCache(data);
      successMsg('Duo status refreshed');
    },
    onError: (error) =>
      errorMsg(error, 'There was an error while refreshing Duo status'),
  });

  if (!visible || !user) {
    return null;
  }

  const badge = getDashboardUserDuoBadge(duoStatus);
  const canSendInvite = isActiveDashboardUser(user);

  return (
    <Card sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Typography variant="h6">Duo Security</Typography>
          <Label color={badge.color}>{badge.label}</Label>
        </Stack>

        <Divider />

        {isLoading ? (
          <Stack spacing={1}>
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="75%" />
          </Stack>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
              },
              columnGap: 2,
              rowGap: 1.5,
            }}
          >
            <DuoDetailItem
              label="Duo 2FA"
              value={duoStatus.duo_2fa_enabled ? 'Enabled' : 'Disabled'}
            />
            <DuoDetailItem
              label="Enrollment"
              value={formatTextValue(duoStatus.duo_enrollment_status)}
            />
            <DuoDetailItem
              label="Invite sent"
              value={formatDateValue(duoStatus.duo_enrollment_sent_at)}
            />
            <DuoDetailItem
              label="Enrolled"
              value={formatDateValue(duoStatus.duo_enrolled_at)}
            />
            <DuoDetailItem
              label="Last synced"
              value={formatDateValue(duoStatus.duo_last_status_synced_at)}
            />
            <DuoDetailItem
              label="Duo found"
              value={formatBooleanValue(duoStatus.duo?.found)}
            />
            <DuoDetailItem
              label="Duo enrolled"
              value={formatBooleanValue(duoStatus.duo?.is_enrolled)}
            />
            <DuoDetailItem
              label="Duo status"
              value={formatTextValue(duoStatus.duo?.status)}
            />
          </Box>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {canSendInvite && (
            <LoadingButton
              type="button"
              variant="contained"
              loading={inviteMutation.isLoading}
              onClick={() => inviteMutation.mutate()}
              startIcon={<Iconify icon="eva:email-outline" />}
            >
              Send Duo Invite
            </LoadingButton>
          )}
          <LoadingButton
            type="button"
            variant="outlined"
            loading={refreshMutation.isLoading}
            onClick={() => refreshMutation.mutate()}
            startIcon={<Iconify icon="eva:refresh-fill" />}
          >
            Refresh Duo Status
          </LoadingButton>
        </Stack>
      </Stack>
    </Card>
  );
}
